/**
 * MD3 Typography Scale — shared between mobile and admin.
 *
 * Two font family roles:
 *   display — serif, used for display and headline text (brand presence)
 *   body    — sans-serif, used for titles, body, labels, UI text (readability)
 *
 * Each TypeStyle specifies which role it uses via the `fontFamily` field.
 * Platform-specific font names are in `defaultFonts`.
 *
 * See docs/architecture/theme-token-roles.md for accessibility requirements.
 */

// ── Font Family System ──

export interface FontFamilies {
  /** Display and headline text — brand presence (serif). */
  display: string;
  /** Body, title, label, UI text — readability (sans-serif). */
  body: string;
  /** Monospace for code/data display if needed. */
  mono: string;
}

/**
 * Default font families per platform.
 *
 * Mobile font names match the expo-google-fonts export names.
 * Admin font names are CSS font-family strings with fallbacks.
 * Playfair Display is a placeholder — swap by changing these values.
 */
export const defaultFonts = {
  mobile: {
    display: 'PlayfairDisplay_400Regular',
    displaySemiBold: 'PlayfairDisplay_600SemiBold',
    displayBold: 'PlayfairDisplay_700Bold',
    body: 'OpenSans_400Regular',
    bodyMedium: 'OpenSans_500Medium',
    bodySemiBold: 'OpenSans_600SemiBold',
    bodyBold: 'OpenSans_700Bold',
    mono: 'SpaceMono_400Regular', // not loaded yet — placeholder
  },
  admin: {
    display: "'Playfair Display', Georgia, 'Times New Roman', serif",
    body: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
  },
} as const;

// ── Type Scale ──

export type FontRole = 'display' | 'body';

export interface TypeStyle {
  fontSize: number;
  lineHeight: number;
  fontWeight: '400' | '500' | '600' | '700';
  letterSpacing: number;
  /** Which font family role this style uses. */
  fontFamily: FontRole;
}

export interface TypographyScale {
  displayLarge: TypeStyle;
  displayMedium: TypeStyle;
  displaySmall: TypeStyle;
  headlineLarge: TypeStyle;
  headlineMedium: TypeStyle;
  headlineSmall: TypeStyle;
  titleLarge: TypeStyle;
  titleMedium: TypeStyle;
  titleSmall: TypeStyle;
  bodyLarge: TypeStyle;
  bodyMedium: TypeStyle;
  bodySmall: TypeStyle;
  labelLarge: TypeStyle;
  labelMedium: TypeStyle;
  labelSmall: TypeStyle;
}

export const typography: TypographyScale = {
  // Display — serif (brand)
  displayLarge:  { fontSize: 57, lineHeight: 64, fontWeight: '400', letterSpacing: 0,     fontFamily: 'display' },
  displayMedium: { fontSize: 45, lineHeight: 52, fontWeight: '400', letterSpacing: 0,     fontFamily: 'display' },
  displaySmall:  { fontSize: 36, lineHeight: 44, fontWeight: '400', letterSpacing: 0,     fontFamily: 'display' },

  // Headline — serif (brand)
  headlineLarge:  { fontSize: 32, lineHeight: 40, fontWeight: '400', letterSpacing: 0, fontFamily: 'display' },
  headlineMedium: { fontSize: 28, lineHeight: 36, fontWeight: '400', letterSpacing: 0, fontFamily: 'display' },
  headlineSmall:  { fontSize: 24, lineHeight: 32, fontWeight: '400', letterSpacing: 0, fontFamily: 'display' },

  // Title — sans-serif (UI)
  titleLarge:  { fontSize: 22, lineHeight: 28, fontWeight: '500', letterSpacing: 0,    fontFamily: 'body' },
  titleMedium: { fontSize: 18, lineHeight: 26, fontWeight: '500', letterSpacing: 0, fontFamily: 'body' },
  titleSmall:  { fontSize: 16, lineHeight: 22, fontWeight: '500', letterSpacing: 0, fontFamily: 'body' },

  // Body — sans-serif (readability)
  bodyLarge:  { fontSize: 18, lineHeight: 28, fontWeight: '400', letterSpacing: 0, fontFamily: 'body' },
  bodyMedium: { fontSize: 17, lineHeight: 26, fontWeight: '400', letterSpacing: 0, fontFamily: 'body' },
  bodySmall:  { fontSize: 15, lineHeight: 22, fontWeight: '400', letterSpacing: 0, fontFamily: 'body' },

  // Label — sans-serif (UI)
  labelLarge:  { fontSize: 16, lineHeight: 22, fontWeight: '500', letterSpacing: 0, fontFamily: 'body' },
  labelMedium: { fontSize: 15, lineHeight: 20, fontWeight: '500', letterSpacing: 0, fontFamily: 'body' },
  labelSmall:  { fontSize: 14, lineHeight: 18, fontWeight: '500', letterSpacing: 0, fontFamily: 'body' },
};
